import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants, Colors, checkPermission } from 'utils';
import { AddIcon, ViewIcon, EditIcon, TrashIcon } from 'assets/icons';
import { HomeService, NewsService } from 'services';
import { Tooltip, Modal, KPICard, ChartCard, DatePicker } from 'components';
import moment from 'moment';
import ModalCreate from '../news/modal-create';
import ModalView from '../news/modal-view';
import ModalUsersDetail from './modal-users-detail';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
	Chart,
	BarController,
	BarElement,
	LineController,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	ArcElement,
	Filler,
	Tooltip as ChartTooltip,
	Legend,
	DoughnutController
} from 'chart.js';

Chart.register(
	BarController,
	BarElement,
	LineController,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	ArcElement,
	Filler,
	ChartTooltip,
	Legend,
	ChartDataLabels,
	DoughnutController
);

interface Props extends RootState, RouteComponentProps {
	dispatch: any
}

// Pills -> period que entiende el backend
const PERIODS = [
	{ key: 'today', label: 'Hoy' },
	{ key: 'week', label: 'Semana' },
	{ key: 'month', label: 'Mes' },
	{ key: 'year', label: 'Año' },
	{ key: 'all', label: 'Todo' },
	{ key: 'custom', label: 'Custom' },
];

// Sub-control de granularidad del chart de usuarios registrados
const GRANULARITIES = [
	{ key: 'day', label: 'Día' },
	{ key: 'week', label: 'Semana' },
	{ key: 'biweek', label: 'Quincena' },
	{ key: 'month', label: 'Mes' },
] as const;

// Atajos rápidos del rango custom -> devuelven { since, until } como Date
const QUICK_RANGES = [
	{
		key: 'last7', label: 'Últimos 7 días',
		range: () => ({ since: moment().subtract(6, 'days').startOf('day').toDate(), until: moment().endOf('day').toDate() })
	},
	{
		key: 'last30', label: 'Últimos 30 días',
		range: () => ({ since: moment().subtract(29, 'days').startOf('day').toDate(), until: moment().endOf('day').toDate() })
	},
	{
		key: 'prevMonth', label: 'Mes anterior',
		range: () => ({
			since: moment().subtract(1, 'month').startOf('month').toDate(),
			until: moment().subtract(1, 'month').endOf('month').toDate()
		})
	},
	{
		key: 'currMonth', label: 'Mes actual',
		range: () => ({ since: moment().startOf('month').toDate(), until: moment().endOf('day').toDate() })
	},
	{
		key: 'currYear', label: 'Año actual',
		range: () => ({ since: moment().startOf('year').toDate(), until: moment().endOf('day').toDate() })
	},
] as const;

// Mapa KPI -> { type backend, título del modal }
const KPI_DRILLDOWN: Record<string, { type: string; title: string }> = {
	active_30d: { type: 'active_30d', title: 'Usuarios activos en últimos 30 días' },
	premium_active: { type: 'premium_active', title: 'Usuarios con plan Premium activo' },
	chat_users: { type: 'chat_users', title: 'Usuarios del chat (últimos 30 días)' },
	new_today: { type: 'new_today', title: 'Nuevos usuarios hoy' },
	new_this_week: { type: 'new_this_week', title: 'Nuevos usuarios últimos 7 días' },
};

// Formatea montos grandes a $X.XM / $X.XK
const formatMoney = (value: number): string => {
	const n = Number(value) || 0;
	if (Math.abs(n) >= 1000000) return `$${ (n / 1000000).toFixed(1) }M`;
	if (Math.abs(n) >= 1000) return `$${ (n / 1000).toFixed(1) }K`;
	return `$${ n.toLocaleString('en-US', { maximumFractionDigits: 0 }) }`;
}

const safeNum = (value: any): number => {
	const n = Number(value);
	return isNaN(n) ? 0 : n;
}

const COLORS = {
	physical: '#ef6526', // naranja
	digital: '#38e788',  // verde brillante
	liquidity: '#4d8eff',// azul
	bar_active: '#54aa3f',
	bar_expired: '#727475',
	bar_expiring: '#ef6526',
	bar_complimentary: '#4d8eff',
	bar_paid: '#38e788',
}

class Home extends React.Component<Props> {

	chartPlanStatus: any = null;
	chartPatrimonio: any = null;
	chartChat: any = null;
	chartMemberships: any = null;
	chartUsersMonth: any = null;
	popoverRef: HTMLDivElement | null = null;

	state = {
		news: [],
		visible: false,
		item: null,
		visible_view: false,
		// legacy
		plans: [],
		chart_bottom_data: [],
		chart_bottom_labels: [],
		total_users: 0,
		total_plans: 0,
		// nuevo
		metrics: null as any,
		filter: { since: '', until: '', period: 'month' },
		period: 'month',
		since: null as Date | null,
		until: null as Date | null,
		chartLoading: false,
		firstLoad: true,
		// granularidad del chart de usuarios registrados (independiente del filtro temporal)
		chartGranularity: 'month' as 'day' | 'week' | 'biweek' | 'month',
		activeQuickRange: '' as string,
		// popover flotante del rango custom (no afecta el flow del documento)
		customPopoverOpen: false,
		draftSince: null as Date | null,
		draftUntil: null as Date | null,
		draftQuickRange: '' as string,
		// drilldown modal
		usersModalVisible: false,
		usersModalType: '',
		usersModalTitle: '',
		usersModalSince: undefined as string | undefined,
		usersModalUntil: undefined as string | undefined,
	}

	componentDidMount() {
		this.props.dispatch({
			type: 'SET_HEADER',
			payload: {
				title: 'Inicio',
				onBack: null
			}
		});

		this.load();

		document.addEventListener('mousedown', this.handleClickOutside);
		document.addEventListener('keydown', this.handleEscape);
	}

	componentWillUnmount() {
		this.destroyCharts();

		document.removeEventListener('mousedown', this.handleClickOutside);
		document.removeEventListener('keydown', this.handleEscape);
	}

	handleClickOutside = (e: MouseEvent) => {
		if (
			this.state.customPopoverOpen &&
			this.popoverRef &&
			!this.popoverRef.contains(e.target as Node)
		) {
			this.closeCustomPopover();
		}
	}

	handleEscape = (e: KeyboardEvent) => {
		if (e.key === 'Escape' && this.state.customPopoverOpen) {
			this.closeCustomPopover();
		}
	}

	destroyCharts = () => {
		[
			this.chartPlanStatus,
			this.chartPatrimonio,
			this.chartChat,
			this.chartMemberships,
			this.chartUsersMonth
		].forEach((c: any) => {
			try { c?.destroy(); } catch (e) { /* noop */ }
		});
	}

	buildParams = () => {
		const { period, since, until, chartGranularity } = this.state;
		const params: any = {
			period,
			chart_granularity: chartGranularity,
			withoutLoading: true
		};

		if (period === 'custom') {
			if (since) params.since = moment(since).format('YYYY-MM-DD');
			if (until) params.until = moment(until).format('YYYY-MM-DD');
		}

		return params;
	}

	load = async (isFilterChange = false) => {
		this.destroyCharts();

		if (isFilterChange) {
			this.setState({ chartLoading: true });
		}

		let res: any = {};

		try {
			res = await HomeService.admin.get(this.buildParams()) || {};
		}
		catch (e) {
			res = {};
		}

		this.setState({
			news: res.news || [],
			plans: res.plans || [],
			chart_bottom_data: res.chart_bottom_data || [],
			chart_bottom_labels: res.chart_bottom_labels || [],
			total_users: res.total_users || 0,
			total_plans: res.total_plans || 0,
			metrics: res.metrics || null,
			filter: res.filter || this.state.filter,
			chartLoading: false,
			firstLoad: false,
		}, () => {
			this.renderCharts();
		});
	}

	setPeriod = (period: string) => {
		if (period === 'custom') {
			// Abre el popover flotante; siembra los drafts con lo ya aplicado
			this.setState({
				customPopoverOpen: true,
				draftSince: this.state.since,
				draftUntil: this.state.until,
				draftQuickRange: this.state.activeQuickRange,
			});
			return;
		}

		// Otro pill -> cierra el popover Y cambia el filtro
		this.setState({ period, customPopoverOpen: false }, () => {
			this.load(true);
		});
	}

	closeCustomPopover = () => {
		// Cierra descartando los drafts (no aplica)
		this.setState({ customPopoverOpen: false });
	}

	// Edición de fecha DENTRO del popover -> solo drafts (no recarga)
	onChangeDraftDate = (key: 'draftSince' | 'draftUntil', date: Date | null) => {
		this.setState({ [key]: date, draftQuickRange: '' } as any);
	}

	// Mini-pill dentro del popover: setea drafts, NO cierra ni recarga
	setDraftQuickRange = (key: string) => {
		const preset = QUICK_RANGES.find((q) => q.key === key);
		if (!preset) return;
		const { since, until } = preset.range();
		this.setState({
			draftSince: since,
			draftUntil: until,
			draftQuickRange: key,
		});
	}

	applyCustomRange = () => {
		const { draftSince, draftUntil, draftQuickRange } = this.state;
		// Commit de los drafts al estado aplicado + recarga + cierra
		this.setState({
			period: 'custom',
			since: draftSince,
			until: draftUntil,
			activeQuickRange: draftQuickRange,
			customPopoverOpen: false,
		}, () => {
			if (this.state.since && this.state.until) {
				this.load(true);
			}
		});
	}

	setGranularity = (chartGranularity: 'day' | 'week' | 'biweek' | 'month') => {
		if (this.state.chartGranularity === chartGranularity) return;
		// Independiente del filtro temporal: NO mostramos loading en las KPIs,
		// solo se redibuja el chart de usuarios registrados.
		this.setState({ chartGranularity }, () => this.load(false));
	}

	openUsersModal = (type: string, title: string, since?: string, until?: string) => {
		this.setState({
			usersModalVisible: true,
			usersModalType: type,
			usersModalTitle: title,
			usersModalSince: since,
			usersModalUntil: until,
		});
	}

	openKpiModal = (kpiKey: string) => {
		const cfg = KPI_DRILLDOWN[kpiKey];
		if (!cfg) return;
		this.openUsersModal(cfg.type, cfg.title);
	}

	closeUsersModal = () => {
		this.setState({ usersModalVisible: false });
	}

	// Calcula el rango since/until de la barra clickeada según la granularidad.
	// Se apoya en el label devuelto por el backend para anclar el período.
	rangeFromBar = (index: number): { since: string; until: string; label: string } | null => {
		const labels: any[] = this.state.chart_bottom_labels || [];
		const label = labels[index];
		if (label == null) return null;

		const g = this.state.chartGranularity;
		let since: moment.Moment;
		let until: moment.Moment;

		if (g === 'day') {
			// label "DD/MM" -> ese día (usamos el año actual como referencia)
			const d = moment(`${ label }/${ moment().year() }`, 'DD/MM/YYYY');
			since = d.clone().startOf('day');
			until = d.clone().endOf('day');
		}
		else if (g === 'week') {
			// label "Sem NN" -> esa semana ISO del año actual
			const wk = parseInt(String(label).replace(/\D/g, ''), 10);
			const base = moment().isoWeek(isNaN(wk) ? moment().isoWeek() : wk);
			since = base.clone().startOf('isoWeek');
			until = base.clone().endOf('isoWeek');
		}
		else if (g === 'biweek') {
			// label "Mmm Q1|Q2" -> primera o segunda quincena del mes
			const parts = String(label).split(' ');
			const mAbbr = parts[0];
			const q = parts[1] === 'Q2' ? 2 : 1;
			const monthMoment = moment(mAbbr, 'MMM', 'es');
			const mm = monthMoment.isValid() ? monthMoment.month() : moment().month();
			const start = moment().month(mm).startOf('month');
			if (q === 1) {
				since = start.clone();
				until = start.clone().date(15).endOf('day');
			}
			else {
				since = start.clone().date(16);
				until = start.clone().endOf('month');
			}
		}
		else {
			// month: label "MMM" (ej "MAY") -> mes completo del año actual
			const monthMoment = moment(String(label), 'MMM', 'es');
			const mm = monthMoment.isValid() ? monthMoment.month() : moment().month();
			since = moment().month(mm).startOf('month');
			until = moment().month(mm).endOf('month');
		}

		return {
			since: since.format('YYYY-MM-DD'),
			until: until.format('YYYY-MM-DD'),
			label: String(label),
		};
	}

	onUsersBarClick = (_event: any, elements: any[]) => {
		if (!elements || elements.length === 0) return;
		const index = elements[0].index;
		const r = this.rangeFromBar(index);
		if (!r) return;
		this.openUsersModal(
			'period_users',
			`Usuarios registrados · ${ r.label }`,
			r.since,
			r.until
		);
	}

	renderCharts = () => {
		const m = this.state.metrics || {};
		try {
			this.renderMembershipsChart();
			this.renderUsersMonthChart();
			this.renderPatrimonioChart(m.patrimonio_summary);
			this.renderChatChart(m.chat_usage);
			this.renderPlanStatusChart(m.plans_status);
		}
		catch (e) {
			// chart de cortesia: nunca crashear el render por chart.js
			console.log(e);
		}
	}

	// ---------- LEGACY CHARTS ----------

	renderMembershipsChart = () => {
		const el: any = document.getElementById('chart');
		if (!el) return;
		this.chartMemberships = new Chart(el, {
			type: 'doughnut',
			data: {
				datasets: (this.state.plans || []).map((item: any) => ({
					label: item.name,
					data: [item.quantity, this.state.total_plans - item.quantity],
					backgroundColor: [item.id == Constants.PLANS.FREE ? Colors.chartFree : Colors.chartPremium, '#000'],
					borderWidth: 5,
					borderColor: '#000',
					borderRadius: 30
				}))
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: '60%',
				plugins: { datalabels: { display: false } }
			}
		});
	}

	renderUsersMonthChart = () => {
		const el: any = document.getElementById('chart-bottom');
		if (!el) return;
		const data = this.state.chart_bottom_data || [];
		const max = data.length ? Math.max(...data) : 0;
		// 'day' devuelve ~30 puntos: rotar labels y limitar ticks para que no se aglutinen
		const isDense = this.state.chartGranularity === 'day';
		this.chartUsersMonth = new Chart(el, {
			type: 'bar',
			data: {
				labels: this.state.chart_bottom_labels || [],
				datasets: [{
					label: 'Total',
					data: data.map((i: number) => max - i),
					backgroundColor: 'rgba(128, 128, 128, 0.5)',
					stack: 'stack1',
					borderRadius: { topLeft: 5, topRight: 5, bottomLeft: 0, bottomRight: 0 },
					order: 2
				}, {
					label: 'Usuarios registrados',
					data: data,
					borderColor: Colors.chartPremium,
					backgroundColor: Colors.chartPremium,
					stack: 'stack1',
					borderRadius: { topLeft: 5, topRight: 5, bottomLeft: 0, bottomRight: 0 },
					// @ts-ignore
					datalabels: { display: false }
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				onClick: (event: any, elements: any[]) => this.onUsersBarClick(event, elements),
				plugins: {
					datalabels: {
						// con 30 puntos (día) los valores sobre las barras se aglutinan
						display: !isDense,
						color: Colors.white,
						anchor: 'end',
						align: 'end',
						font: { weight: 'bold', size: 14 },
						formatter: function (value: any, context: any) {
							const dataIndex = context.dataIndex;
							const greenData = context.chart.data.datasets[1].data;
							return greenData[dataIndex];
						}
					},
					legend: { display: false }
				},
				scales: {
					x: {
						grid: { display: false },
						// @ts-ignore
						border: { display: true, width: 1, color: 'white', side: 'bottom' },
						ticks: {
							display: true,
							color: 'white',
							font: { weight: 'bold', size: isDense ? 10 : 12 },
							autoSkip: true,
							maxTicksLimit: isDense ? 12 : 14,
							maxRotation: isDense ? 45 : 0,
							minRotation: isDense ? 45 : 0
						}
					},
					y: { display: false, suggestedMax: max * 1.15 }
				}
			}
		});
	}

	// ---------- NEW CHARTS ----------

	renderPatrimonioChart = (summary: any) => {
		const el: any = document.getElementById('chart-patrimonio');
		if (!el) return;

		const byType = summary?.by_type || {};
		const physical = safeNum(byType.physical);
		const digital = safeNum(byType.digital);
		const liquidity = safeNum(byType.liquidity);

		this.chartPatrimonio = new Chart(el, {
			type: 'doughnut',
			data: {
				labels: ['Físico', 'Digital', 'Liquidez'],
				datasets: [{
					data: [physical, digital, liquidity],
					backgroundColor: [COLORS.physical, COLORS.digital, COLORS.liquidity],
					borderWidth: 4,
					borderColor: '#171a1f',
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: '64%',
				plugins: {
					datalabels: { display: false },
					legend: { display: false }
				}
			}
		});
	}

	renderChatChart = (usage: any) => {
		const el: any = document.getElementById('chart-chat');
		if (!el) return;

		const breakdown = Array.isArray(usage?.daily_breakdown) ? usage.daily_breakdown : [];
		const labels = breakdown.map((d: any) => moment(d?.date).format('DD/MM'));
		const data = breakdown.map((d: any) => safeNum(d?.count));

		this.chartChat = new Chart(el, {
			type: 'line',
			data: {
				labels,
				datasets: [{
					label: 'Mensajes',
					data,
					borderColor: COLORS.bar_active,
					backgroundColor: 'rgba(84, 170, 63, 0.18)',
					fill: true,
					tension: 0.35,
					pointRadius: 2,
					pointBackgroundColor: COLORS.bar_paid,
					borderWidth: 2,
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					datalabels: { display: false },
					legend: { display: false }
				},
				scales: {
					x: {
						grid: { display: false },
						ticks: { color: 'rgba(255,255,255,0.45)', maxTicksLimit: 8, font: { size: 10 } }
					},
					y: {
						beginAtZero: true,
						grid: { color: 'rgba(255,255,255,0.05)' },
						ticks: { color: 'rgba(255,255,255,0.45)', precision: 0, font: { size: 10 } }
					}
				}
			}
		});
	}

	renderPlanStatusChart = (status: any) => {
		const el: any = document.getElementById('chart-plan-status');
		if (!el) return;

		const s = status || {};
		this.chartPlanStatus = new Chart(el, {
			type: 'bar',
			data: {
				labels: ['Activos', 'Vencidos', 'Por vencer', 'Cortesía', 'Pagados'],
				datasets: [{
					label: 'Planes',
					data: [
						safeNum(s.active),
						safeNum(s.expired),
						safeNum(s.expiring_soon),
						safeNum(s.complimentary_active),
						safeNum(s.paid_active)
					],
					backgroundColor: [
						COLORS.bar_active,
						COLORS.bar_expired,
						COLORS.bar_expiring,
						COLORS.bar_complimentary,
						COLORS.bar_paid
					],
					borderRadius: 6,
					maxBarThickness: 54,
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					datalabels: {
						color: '#fff',
						anchor: 'end',
						align: 'end',
						font: { weight: 'bold', size: 13 },
						formatter: (v: any) => v
					},
					legend: { display: false }
				},
				scales: {
					x: {
						grid: { display: false },
						ticks: { color: 'rgba(255,255,255,0.6)', font: { size: 11 } }
					},
					y: { display: false, beginAtZero: true }
				}
			}
		});
	}

	// ---------- NEWS (legacy) ----------

	addNew = () => {
		this.props.history.replace('/admin/news?create=true');
	}

	viewNew = (e: any, item: any) => {
		if (e) e.preventDefault();
		this.setState({ visible_view: true, item });
	}

	editNew = (e: any, item: any) => {
		if (e) e.preventDefault();
		this.setState({ visible: true, visible_view: false, item });
	}

	deleteNew = (e: any, item: any) => {
		if (e) e.preventDefault();
		Globals.confirm('¿Desea eliminar la noticia?', async () => {
			await NewsService.admin.delete({ news_id: item.id });
			this.setState({ visible_view: false, item: null });
			this.load();
		});
	}

	onClose = () => {
		this.setState({ visible: false, item: null }, () => this.load());
	}

	onCloseView = () => {
		this.setState({ visible_view: false, item: null }, () => this.load());
	}

	render() {
		const { news, visible, visible_view, plans, metrics, period, chartLoading } = this.state;

		// Defensive: metrics puede venir null -> degradación elegante
		const m = metrics || {};
		const patrimonio = m.patrimonio_summary || {};
		const activeUsers = m.active_users || {};
		const planStatus = m.plans_status || {};
		const chatUsage = m.chat_usage || {};
		const growth = m.user_growth_today_week || {};
		const topChat = Array.isArray(m.top_chat_users) ? m.top_chat_users : [];

		return (
			<div id="admin-home">
				<Modal
					className="modal-create-news"
					visible={ visible }
					title={ (this.state.item ? 'Editar' : 'Nueva') + ' noticia' }
					onClose={ () => this.onClose() }
				>
					<ModalCreate
						item={ this.state.item }
						onClose={ () => this.onClose() } />
				</Modal>

				<Modal
					className="modal-view-news"
					visible={ visible_view }
					// @ts-ignore
					title={ this.state.item?.title }
					onClose={ () => this.onCloseView() }
				>
					<ModalView
						onEdit={ this.editNew }
						onDelete={ this.deleteNew }
						item={ this.state.item }
						onClose={ () => this.onCloseView() } />
				</Modal>

				<ModalUsersDetail
					visible={ this.state.usersModalVisible }
					title={ this.state.usersModalTitle }
					type={ this.state.usersModalType }
					since={ this.state.usersModalSince }
					until={ this.state.usersModalUntil }
					onClose={ this.closeUsersModal } />

				{ /* FILA 1 — Header + Selector temporal */ }
				<div className="dashboard-toolbar">
					<h2 className="dashboard-title">Dashboard</h2>
					<div className="period-pills">
						{
							PERIODS.map((p) => {
								if (p.key === 'custom') {
									return (
										<div className="period-pills-wrapper" key={ p.key }>
											<button
												className={ `period-pill ${ period === 'custom' ? 'active' : '' }` }
												onClick={ () => this.setPeriod('custom') }>
												{ p.label }
											</button>

											{
												this.state.customPopoverOpen && (
													<div
														className="custom-range-popover"
														ref={ (el) => { this.popoverRef = el; } }>
														<div className="popover-header">
															<span>Rango personalizado</span>
														</div>
														<div className="popover-body">
															<div className="custom-range-row">
																<div className="custom-range-field">
																	<DatePicker
																		label="Desde"
																		color="white"
																		placeholder="Desde"
																		value={ this.state.draftSince }
																		maxDate={ this.state.draftUntil || new Date() }
																		onChange={ (d: Date) => this.onChangeDraftDate('draftSince', d) } />
																</div>
																<div className="custom-range-field">
																	<DatePicker
																		label="Hasta"
																		color="white"
																		placeholder="Hasta"
																		value={ this.state.draftUntil }
																		minDate={ this.state.draftSince || undefined }
																		maxDate={ new Date() }
																		onChange={ (d: Date) => this.onChangeDraftDate('draftUntil', d) } />
																</div>
															</div>
															<div className="quick-ranges">
																<span className="quick-ranges-label">Atajos rápidos:</span>
																<div className="quick-ranges-pills">
																	{
																		QUICK_RANGES.map((q) => (
																			<button
																				key={ q.key }
																				className={ `quick-range-pill ${ this.state.draftQuickRange === q.key ? 'active' : '' }` }
																				onClick={ () => this.setDraftQuickRange(q.key) }>
																				{ q.label }
																			</button>
																		))
																	}
																</div>
															</div>
														</div>
														<div className="popover-footer">
															<button
																className="popover-btn popover-btn-cancel"
																onClick={ this.closeCustomPopover }>
																Cancelar
															</button>
															<button
																className="popover-btn popover-btn-apply"
																onClick={ this.applyCustomRange }>
																Aplicar
															</button>
														</div>
													</div>
												)
											}
										</div>
									)
								}

								return (
									<button
										key={ p.key }
										className={ `period-pill ${ period === p.key ? 'active' : '' }` }
										onClick={ () => this.setPeriod(p.key) }>
										{ p.label }
									</button>
								)
							})
						}
					</div>
				</div>

				{ /* FILA 2 — 4 KPI principales */ }
				<div className="row">
					<div className="col-md-3 col-sm-6 kpi-col">
						<KPICard
							title="Patrimonio Total"
							value={ formatMoney(safeNum(patrimonio.total)) }
							subtitle={ `${ safeNum(patrimonio.users_with_data) } usuarios con activos` }
							variant="highlight" />
					</div>
					<div className="col-md-3 col-sm-6 kpi-col">
						<div
							className="kpi-clickable"
							role="button"
							tabIndex={ 0 }
							onClick={ () => this.openKpiModal('active_30d') }
							onKeyDown={ (e: any) => { if (e.key === 'Enter') this.openKpiModal('active_30d'); } }>
							<KPICard
								title="Usuarios Activos 30d"
								loading={ chartLoading }
								value={ `${ safeNum(activeUsers.last_30d) } / ${ safeNum(this.state.total_users) }` }
								subtitle={ `${ safeNum(activeUsers.percent_total).toFixed(2) }% del total` }
								variant="success" />
						</div>
					</div>
					<div className="col-md-3 col-sm-6 kpi-col">
						<div
							className="kpi-clickable"
							role="button"
							tabIndex={ 0 }
							onClick={ () => this.openKpiModal('premium_active') }
							onKeyDown={ (e: any) => { if (e.key === 'Enter') this.openKpiModal('premium_active'); } }>
							<KPICard
								title="Premium Activos"
								loading={ chartLoading }
								value={ safeNum(planStatus.paid_active) }
								subtitle={ `${ safeNum(planStatus.complimentary_active) } cortesía` }
								variant="default" />
						</div>
					</div>
					<div className="col-md-3 col-sm-6 kpi-col">
						<div
							className="kpi-clickable"
							role="button"
							tabIndex={ 0 }
							onClick={ () => this.openKpiModal('chat_users') }
							onKeyDown={ (e: any) => { if (e.key === 'Enter') this.openKpiModal('chat_users'); } }>
							<KPICard
								title="Mensajes Chat"
								loading={ chartLoading }
								value={ safeNum(chatUsage.total_messages) }
								subtitle={ `${ safeNum(chatUsage.unique_users) } usuarios` }
								variant="default" />
						</div>
					</div>
				</div>

				{ /* FILA 3 — 2 KPI de crecimiento */ }
				<div className="row">
					<div className="col-md-6 kpi-col">
						<div
							className="kpi-clickable"
							role="button"
							tabIndex={ 0 }
							onClick={ () => this.openKpiModal('new_today') }
							onKeyDown={ (e: any) => { if (e.key === 'Enter') this.openKpiModal('new_today'); } }>
							<KPICard
								title="Nuevos Usuarios Hoy"
								loading={ chartLoading }
								size="large"
								value={ safeNum(growth.today) }
								subtitle={ `${ safeNum(growth.yesterday) } ayer` }
								trend={ {
									value: safeNum(growth.delta_today_pct),
									label: 'vs ayer',
									positive_is_good: true
								} } />
						</div>
					</div>
					<div className="col-md-6 kpi-col">
						<div
							className="kpi-clickable"
							role="button"
							tabIndex={ 0 }
							onClick={ () => this.openKpiModal('new_this_week') }
							onKeyDown={ (e: any) => { if (e.key === 'Enter') this.openKpiModal('new_this_week'); } }>
							<KPICard
								title="Crecimiento Semanal"
								loading={ chartLoading }
								size="large"
								value={ safeNum(growth.this_week) }
								subtitle={ `${ safeNum(growth.last_week) } semana pasada` }
								trend={ {
									value: safeNum(growth.delta_week_pct),
									label: 'vs semana anterior',
									positive_is_good: true
								} } />
						</div>
					</div>
				</div>

				{ /* FILA 4 — Patrimonio por tipo + Mensajes chat */ }
				<div className="row">
					<div className="col-md-6 chart-col">
						<ChartCard title="Patrimonio por Tipo" subtitle="Distribución de activos">
							<div className="patrimonio-chart-wrap">
								<div className="container-chart-circle">
									<canvas id="chart-patrimonio"></canvas>
									<div className="container-total">
										<h3>Total</h3>
										<h4>{ formatMoney(safeNum(patrimonio.total)) }</h4>
									</div>
								</div>
								<div className="patrimonio-legend">
									{
										[
											{ label: 'Físico', color: COLORS.physical, val: safeNum(patrimonio?.by_type?.physical) },
											{ label: 'Digital', color: COLORS.digital, val: safeNum(patrimonio?.by_type?.digital) },
											{ label: 'Liquidez', color: COLORS.liquidity, val: safeNum(patrimonio?.by_type?.liquidity) },
										].map((seg) => {
											const total = safeNum(patrimonio.total) || 1;
											const pct = ((seg.val / total) * 100).toFixed(1);
											return (
												<div className="legend-item" key={ seg.label }>
													<span className="legend-dot" style={ { backgroundColor: seg.color } }></span>
													<span className="legend-label">{ seg.label }</span>
													<span className="legend-value">{ formatMoney(seg.val) } · { pct }%</span>
												</div>
											)
										})
									}
								</div>
							</div>
						</ChartCard>
					</div>
					<div className="col-md-6 chart-col">
						<ChartCard
							title="Mensajes Chat"
							subtitle="Últimos 30 días"
							loading={ chartLoading }>
							<div className="canvas-wrap">
								<canvas id="chart-chat"></canvas>
							</div>
						</ChartCard>
					</div>
				</div>

				{ /* FILA 5 — Estado de planes + Top chat users */ }
				<div className="row">
					<div className="col-md-6 chart-col">
						<ChartCard
							title="Estado de Planes"
							subtitle="Distribución actual"
							loading={ chartLoading }>
							<div className="canvas-wrap">
								<canvas id="chart-plan-status"></canvas>
							</div>
						</ChartCard>
					</div>
					<div className="col-md-6 chart-col">
						<ChartCard
							title="Top 10 Usuarios del Chat"
							subtitle={ `${ safeNum(chatUsage.unique_users) } usuarios activos` }
							loading={ chartLoading }>
							<div className="top-chat-table">
								<table className="table">
									<thead>
										<tr>
											<th>#</th>
											<th>Nombre</th>
											<th>Email</th>
											<th className="text-right">Msj</th>
											<th className="text-right">Plan</th>
										</tr>
									</thead>
									<tbody>
										{
											topChat.slice(0, 10).map((u: any, index: number) => (
												<tr key={ u?.user_id || index }>
													<td className="rank">{ index + 1 }</td>
													<td className="ellipsis">{ [u?.name, u?.lastname].filter(Boolean).join(' ') || '—' }</td>
													<td className="ellipsis email">{ u?.email || '—' }</td>
													<td className="text-right bold">{ safeNum(u?.message_count) }</td>
													<td className="text-right">
														{
															u?.has_active_plan ? (
																<span className="badge-premium">Premium</span>
															) : (
																<span className="badge-free">Free</span>
															)
														}
													</td>
												</tr>
											))
										}
										{
											topChat.length === 0 && (
												<tr>
													<td colSpan={ 5 } className="no-items">No se encontraron registros</td>
												</tr>
											)
										}
									</tbody>
								</table>
							</div>
						</ChartCard>
					</div>
				</div>

				{ /* FILA 6 — Charts legacy */ }
				<div className="row">
					<div className="col-md-6 chart-col">
						<ChartCard title="Membresías Adquiridas">
							<div className="memberships-wrap">
								<div className="container-chart-circle">
									<canvas id="chart"></canvas>
									<div className="container-total">
										<h3>Total</h3>
										<h4>{ this.state.total_plans }</h4>
									</div>
								</div>
								<div className="memberships-legend">
									{
										plans.map((item: any, index: number) => (
											<div className="item-plan" key={ index }>
												<div className={ `dot-plan ${ item.id == Constants.PLANS.FREE ? '' : 'green' }` }></div>
												<div>
													<p>{ item.name }</p>
													<p className="bold">{ item.quantity }</p>
												</div>
											</div>
										))
									}
								</div>
							</div>
						</ChartCard>
					</div>
					<div className="col-md-6 chart-col">
						<ChartCard
							title="Usuarios Registrados"
							subtitle={ `Total ${ this.state.total_users }` }
							actions={
								<div className="granularity-pills">
									{
										GRANULARITIES.map((g) => (
											<button
												key={ g.key }
												className={ `granularity-pill ${ this.state.chartGranularity === g.key ? 'active' : '' }` }
												onClick={ () => this.setGranularity(g.key) }>
												{ g.label }
											</button>
										))
									}
								</div>
							}>
							<div className="canvas-wrap canvas-clickable">
								<canvas id="chart-bottom"></canvas>
							</div>
						</ChartCard>
					</div>
				</div>

				{ /* FILA 7 — Noticias */ }
				<div className="row">
					<div className="col-md-12 chart-col">
						<ChartCard
							title="Noticias Registradas"
							actions={
								<button className="btn btn-add" onClick={ this.addNew }>
									<img src={ AddIcon } alt="" />
									Nuevo
								</button>
							}>
							{
								news.map((item: any, index: number) => (
									<div className="item-news" key={ index }>
										{
											item?.image && (
												<div>
													<div className="container-image">
														<img className="image" src={ Constants.BASE_STORAGE + item?.image } alt="" />
													</div>
												</div>
											)
										}
										<div style={ { flex: 1, overflow: 'hidden', paddingRight: '10px' } }>
											<p className="name">{ item?.title }</p>
											<p className="date">{ moment(item?.created_at).format('DD/MM/YYYY') }</p>
										</div>
										<Tooltip title="Ver">
											<a className="link-icon" href="#" onClick={ (e: any) => this.viewNew(e, item) }>
												<img src={ ViewIcon } alt="" />
											</a>
										</Tooltip>
										{
											checkPermission(Constants.MODULES.NEWS, 'delete') && (
												<Tooltip title="Editar">
													<a className="link-icon" href="#" onClick={ (e: any) => this.editNew(e, item) }>
														<img src={ EditIcon } alt="" />
													</a>
												</Tooltip>
											)
										}
										{
											checkPermission(Constants.MODULES.NEWS, 'delete') && (
												<Tooltip title="Eliminar">
													<a className="link-icon" href="#" onClick={ (e: any) => this.deleteNew(e, item) }>
														<img src={ TrashIcon } alt="" />
													</a>
												</Tooltip>
											)
										}
									</div>
								))
							}
							{
								news.length === 0 && (
									<p className="no-items">No se encontraron registros</p>
								)
							}
						</ChartCard>
					</div>
				</div>
			</div>
		)
	}
}

export default connect((state: RootState) => {
	return {
		user: state.user
	}
})(Home);
